import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { formatDate, formatNumber } from '../utils/helpers';
import {
  Key,
  Plus,
  Trash2,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Copy
} from 'lucide-react';

const AuthCodes = () => {
  const [authCodes, setAuthCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [newAuthCode, setNewAuthCode] = useState({
    code: '',
    description: '',
    expiresAt: ''
  });
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchAuthCodes();
  }, []);

  const fetchAuthCodes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/auth-codes');
      setAuthCodes(response.data.authCodes);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Fetch auth codes error:', error);
      toast.error('Failed to load auth codes');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAuthCode = async (e) => {
    e.preventDefault();
    
    if (!newAuthCode.code.trim()) {
      toast.error('Auth code is required');
      return;
    }

    try {
      setActionLoading(true);
      await api.post('/admin/auth-codes', {
        code: newAuthCode.code.trim(),
        description: newAuthCode.description.trim(),
        expiresAt: newAuthCode.expiresAt || null
      });
      
      toast.success('Auth code added successfully');
      setShowAddModal(false);
      setNewAuthCode({ code: '', description: '', expiresAt: '' });
      fetchAuthCodes();
    } catch (error) {
      console.error('Add auth code error:', error);
      toast.error(error.response?.data?.message || 'Failed to add auth code');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteAuthCode = async (id, code) => {
    if (!window.confirm(`Are you sure you want to delete auth code "${code}"?`)) {
      return;
    }

    try {
      await api.delete(`/admin/auth-codes/${id}`);
      toast.success('Auth code deleted successfully');
      fetchAuthCodes();
    } catch (error) {
      console.error('Delete auth code error:', error);
      toast.error('Failed to delete auth code');
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const getStatusBadge = (authCode) => {
    if (!authCode.isActive) {
      return <span className="badge badge-error">Inactive</span>;
    }
    if (authCode.expiresAt && new Date(authCode.expiresAt) < new Date()) {
      return <span className="badge badge-error">Expired</span>;
    }
    return <span className="badge badge-success">Active</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Auth Codes</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage authentication codes for API requests
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={fetchAuthCodes}
            className="btn-secondary"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Auth Code
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Key className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Codes</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumber(stats.total || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Codes</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumber(stats.active || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 bg-red-100 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Expired Codes</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumber(stats.expired || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <AlertCircle className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Inactive Codes</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumber(stats.inactive || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Auth Codes Table */}
      <div className="card">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Auth Code</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Expires At</th>
                  <th>Usage Count</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {authCodes.map((authCode) => (
                  <tr key={authCode._id} className="hover:bg-gray-50">
                    <td>
                      <div className="flex items-center space-x-2">
                        <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                          {authCode.code}
                        </code>
                        <button
                          onClick={() => copyToClipboard(authCode.code)}
                          className="text-gray-400 hover:text-blue-600"
                          title="Copy code"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                    <td>
                      <div className="text-sm text-gray-900">
                        {authCode.description || '-'}
                      </div>
                    </td>
                    <td>{getStatusBadge(authCode)}</td>
                    <td>
                      <div className="text-sm text-gray-900">
                        {authCode.expiresAt ? formatDate(authCode.expiresAt) : 'Never'}
                      </div>
                    </td>
                    <td>
                      <div className="text-sm text-gray-900">
                        {formatNumber(authCode.usageCount || 0)}
                      </div>
                    </td>
                    <td>
                      <div className="text-sm text-gray-900">
                        {formatDate(authCode.createdAt)}
                      </div>
                    </td>
                    <td>
                      <button
                        onClick={() => handleDeleteAuthCode(authCode._id, authCode.code)}
                        className="text-gray-400 hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add Auth Code Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div 
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={() => setShowAddModal(false)}
            ></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleAddAuthCode}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center mb-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Key className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="ml-3 text-lg font-medium text-gray-900">
                      Add New Auth Code
                    </h3>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Auth Code *
                      </label>
                      <input
                        type="text"
                        className="input"
                        value={newAuthCode.code}
                        onChange={(e) => setNewAuthCode(prev => ({ ...prev, code: e.target.value }))}
                        placeholder="Enter auth code"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        className="input"
                        rows="3"
                        value={newAuthCode.description}
                        onChange={(e) => setNewAuthCode(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Optional description"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Expires At (Optional)
                      </label>
                      <input
                        type="datetime-local"
                        className="input"
                        value={newAuthCode.expiresAt}
                        onChange={(e) => setNewAuthCode(prev => ({ ...prev, expiresAt: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={actionLoading}
                    className="btn-primary w-full sm:w-auto sm:ml-3"
                  >
                    {actionLoading ? 'Adding...' : 'Add Auth Code'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="btn-secondary w-full sm:w-auto mt-3 sm:mt-0"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthCodes;
